import { Alert, Form, FormInstance, FormItemProps, Input } from 'antd';
import { NamePath } from 'antd/es/form/interface';
import { rules } from './rules';
import { FC, useEffect, useState } from 'react';

export function useForm<T = unknown>() {
	const [form] = Form.useForm<T>();
	const value = Form.useWatch(null, form) as Partial<T> | null;

	const fieldName = (name: NamePath<T>) => name;

	return {
		form,
		value,
		fieldName,
	};
}

export function useFormAlert<T = unknown>(
	form: FormInstance<T>,
	fieldName: NamePath<T> | null,
	checkDirty = true,
) {
	const [alerts, setAlerts] = useState<string[]>([]);
	const values = Form.useWatch<T>(fieldName, form);

	const validate = async () => {
		return form
			.validateFields([fieldName], {
				validateOnly: true,
				recursive: true,
			})
			.then(() => {
				setAlerts([]);
			})
			.catch((err) => {
				const messages =
					err?.errorFields
						?.map?.((err: object) => 'errors' in err && err?.errors)
						?.flat?.() ?? [];
				setAlerts(messages);
			});
	};

	useEffect(() => {
		if (checkDirty && !form.isFieldsTouched()) return;
		validate();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form, values]);

	return { alerts, validate };
}

export const FormAlert: FC<{ alerts: string[] }> = ({ alerts = [] }) => {
	if (!alerts.length) return null;
	if (alerts.length === 1) {
		return (
			<Alert
				message={alerts[0]}
				type="warning"
				className="custom-alert"
				showIcon
			/>
		);
	}

	return (
		<Alert
			message={
				<ul style={{ margin: 0, paddingLeft: 16 }}>
					{alerts.map((alert, index) => (
						<li key={index}>{alert}</li>
					))}
				</ul>
			}
			type="warning"
			className="custom-alert"
			showIcon
		/>
	);
};

export const formItem = {
	npwp: {
		rules: [...rules.npwp],
		getValueProps: (value) => {
			const format = (val: string) => {
				const npwp = val?.replace(/\D+/g, '');

				if (npwp.length >= 13) {
					return npwp?.replace(
						/(\d{2})(\d{3})(\d{3})(\d{1})(\d{3})/,
						'$1.$2.$3.$4-$5.',
					);
				}

				if (npwp.length >= 13) {
					return npwp?.replace(
						/(\d{2})(\d{3})(\d{3})(\d{1})(\d{3})/,
						'$1.$2.$3.$4-$5.',
					);
				}

				if (npwp.length >= 10) {
					return npwp?.replace(/(\d{2})(\d{3})(\d{3})(\d{1})/, '$1.$2.$3.$4-');
				}

				if (npwp.length >= 9) {
					return npwp?.replace(/(\d{2})(\d{3})(\d{3})/, '$1.$2.$3.');
				}

				if (npwp.length >= 6) {
					return npwp?.replace(/(\d{2})(\d{3})/, '$1.$2.');
				}

				if (npwp.length >= 3) {
					return npwp?.replace(/(\d{2})/, '$1.');
				}

				return npwp;
			};

			return {
				value: value && format(value),
			};
		},
		normalize: (value) => value?.replace(/\D+/g, '')?.substring(16, 0),
		children: <Input />,
	} as FormItemProps,
};
